"use client"

import { useState, useEffect } from "react"
import { SiteService, type MapData, type CreateMapRequest } from "@/services/SiteService"
import { TenantService, type Tenant } from "@/services/TenantService"

export function useMaps() {
  const [maps, setMaps] = useState<MapData[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMaps = async () => {
    try {
      setLoading(true)
      setError(null)
      const userMaps = await SiteService.getUserMaps()
      setMaps(userMaps)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch maps")
    } finally {
      setLoading(false)
    }
  }

  const fetchTenants = async (): Promise<Tenant[]> => {
    try {
      setLoading(true)
      setError(null)
      const tenants = await TenantService.getMyTenants()
      setTenants(tenants)
      return tenants
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tenants")
      return []
    } finally {
      setLoading(false)
    }
  }

  const createMap = async (data: CreateMapRequest): Promise<MapData | null> => {
    try {
      setError(null)
      const newMap = await SiteService.createMap(data)
      setMaps((prev) => [newMap, ...prev])
      return newMap
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create map")
      return null
    }
  }

  const deleteMap = async (mapId: string): Promise<boolean> => {
    try {
      setError(null)
      await SiteService.deleteMap(mapId)
      setMaps((prev) => prev.filter((map) => map.id !== mapId))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete map")
      return false
    }
  }

  useEffect(() => {
    fetchMaps()
  }, [])

  return {
    maps,
    tenants,
    loading,
    error,
    fetchMaps,
    fetchTenants,
    createMap,
    deleteMap,
  }
}
